import React from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../Components/Request';
import Card from '../../../components/ui/Card/Card';
import Input from '../../../components/ui/Input/Input';
import { SearchIcon } from '../../../components/ui/Icons';
import { FEATURE_MULTIMODAL } from '../../../featureFlags';
import { statusColor } from '../../../notifications/visuals';
import styles from './ShipmentsColumn.module.css';

/**
 * ENVÍOS DEL CLIENTE — las tres modalidades en una columna.
 *
 * ## 🔴 Qué estaba mal antes
 *
 * La columna decía "Loads" y traía **solo terrestre**, con **todas** las cargas sin límite. Dos problemas:
 *
 * 1. **Mentía por omisión.** Un cliente con marítimo y aéreo veía una lista que se veía completa y no lo era.
 *    Desde la pantalla no había forma de notar que faltaban.
 * 2. **No escalaba.** Miles de filas viajando en cada apertura para pintar una lista que nadie recorre entera.
 *
 * ## 🔑 Por qué una columna y no tres
 *
 * La pregunta al abrir la ficha es *"¿qué le estamos moviendo a este cliente?"*, no *"¿qué le movemos por mar?"*.
 * Tres columnas obligarían a sumar a ojo, y dos estarían vacías para la mayoría de los clientes —hoy casi todos son
 * solo terrestres—, así que la pantalla se vería **rota** en lugar de simple.
 *
 * 📌 Cada renglón dice de qué modalidad es, y el filtro de arriba solo ofrece las modalidades que **ese cliente
 * tiene**: un botón "Air" que siempre devuelve vacío es ruido.
 */

type Mode = 'load' | 'ocean' | 'air';

/**
 * Las modalidades que esta columna pide, cuenta y ofrece.
 *
 * 🔄 **Sin `FEATURE_MULTIMODAL`, solo terrestre** (14-oct-2026): marítimo y aéreo no se publican todavía, y su detalle
 * no existe en ese ambiente, así que un renglón suyo llevaría a una pantalla que no hay. Se pide `mode: 'load'` al
 * servidor en lugar de filtrar aquí, para que la página traiga 20 cargas y no 20 renglones de los que se tiran algunos.
 */
const MODES: Mode[] = FEATURE_MULTIMODAL ? ['load', 'ocean', 'air'] : ['load'];

interface Row {
  id: string;
  mode: Mode;
  reference: string;
  status: string | null;
  expectedAt: string | null;
  createdAt: string;
}

interface Counts {
  load: number;
  ocean: number;
  air: number;
}

/**
 * Cómo se distingue cada modalidad.
 *
 * 🔑 **Letra y color, no solo color.** Un badge que solo se distingue por tono deja fuera a quien no separa bien
 * los colores, y además se pierde en una captura de pantalla en blanco y negro — que es cómo se comparten estos
 * casos por WhatsApp cuando algo no cuadra.
 */
const MODE: Record<Mode, { tag: string; label: string; tone: string; path: string }> = {
  load: { tag: 'LND', label: 'Land', tone: 'land', path: '/guide/detail' },
  ocean: { tag: 'OCN', label: 'Ocean', tone: 'ocean', path: '/shipments' },
  air: { tag: 'AIR', label: 'Air', tone: 'air', path: '/shipments' },
};

const PAGE_SIZE = 20;

const shortDay = (value: string | null): string => {
  if (!value) return '';
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const ShipmentsColumn: React.FC<{ clientId: string; canOpen: boolean }> = ({
  clientId,
  canOpen,
}) => {
  const navigate = useNavigate();

  const [rows, setRows] = React.useState<Row[]>([]);
  const [counts, setCounts] = React.useState<Counts>({ load: 0, ocean: 0, air: 0 });
  const [mode, setMode] = React.useState<'all' | Mode>('all');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  /**
   * Las cargas terrestres no se listan porque el cliente no está asignado a quien mira (PENDIENTES_TERRESTRE, punto 10;
   * 13-oct-2026). Se dice, para que "0 terrestres" no se lea como que el cliente no tiene.
   */
  const [landHidden, setLandHidden] = React.useState(false);

  /**
   * ⚠️ La búsqueda va al servidor, así que se **espera** antes de pedir.
   *
   * Sin esta espera, escribir "DYLO-1001" son diez peticiones y las respuestas pueden llegar desordenadas: la de
   * "DYLO-100" después de la de "DYLO-1001", y la lista termina mostrando resultados de un texto que ya no está en
   * la caja.
   */
  const [debounced, setDebounced] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  /** 📌 Cambiar el filtro o la búsqueda vuelve a la primera página: quedarse en la 3 mostraría vacío. */
  React.useEffect(() => {
    setPage(1);
  }, [debounced, mode]);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);

    axiosInstance
      .get(`/clients/${clientId}/shipments`, {
        params: {
          page,
          pageSize: PAGE_SIZE,
          search: debounced || undefined,
          mode: FEATURE_MULTIMODAL ? mode : 'load',
        },
      })
      .then(({ data }) => {
        if (!alive) return;
        setRows(data?.items || []);
        setHasMore(Boolean(data?.hasMore));
        if (data?.counts) setCounts(data.counts);
        setLandHidden(Boolean(data?.landHidden));
      })
      .catch(() => {
        if (alive) {
          setRows([]);
          setHasMore(false);
        }
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [clientId, page, debounced, mode]);

  const total = MODES.reduce((sum, m) => sum + counts[m], 0);

  /**
   * 🔑 Solo se ofrecen las modalidades que este cliente **tiene**. Un filtro "Air" en un cliente que nunca ha
   * volado nada solo puede devolver vacío, y un filtro que devuelve vacío se lee como un fallo.
   */
  const available = MODES.filter((m) => counts[m] > 0);

  const open = (row: Row) => {
    if (!canOpen) return;
    navigate(`${MODE[row.mode].path}/${row.id}`);
  };

  return (
    <Card className={styles.col}>
      <div className={styles.head}>
        <h3 className={styles.title}>
          Shipments
          {/**
           * 📌 El total va en el título. Es el dato que se busca al abrir la ficha —*"¿cuánto le movemos?"*— y sin
           * él habría que contar renglones de una lista paginada, que no se puede.
           */}
          {total > 0 && <span className={styles.total}>{total}</span>}
        </h3>
      </div>

      {/** Los filtros solo aparecen si hay más de una modalidad: con una sola no hay nada que filtrar. */}
      {available.length > 1 && (
        <div className={styles.modes}>
          <button
            type="button"
            className={[styles.modeBtn, mode === 'all' ? styles.modeOn : ''].join(' ')}
            onClick={() => setMode('all')}
          >
            All {total}
          </button>
          {available.map((m) => (
            <button
              key={m}
              type="button"
              className={[
                styles.modeBtn,
                styles[`mode_${MODE[m].tone}`],
                mode === m ? styles.modeOn : '',
              ].join(' ')}
              onClick={() => setMode(m)}
            >
              {MODE[m].label} {counts[m]}
            </button>
          ))}
        </div>
      )}

      <div className={styles.search}>
        <Input
          icon={<SearchIcon size={16} />}
          placeholder="Search by Ref"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {landHidden && (
        <p className={styles.hiddenNote} role="note">
          Land loads are listed only for clients assigned to you.
        </p>
      )}

      <div className={styles.list}>
        {loading && <p className={styles.empty}>Loading…</p>}

        {!loading && rows.length === 0 && (
          <p className={styles.empty}>
            {debounced ? 'Nothing matches that reference.' : 'No shipments for this client yet.'}
          </p>
        )}

        {!loading &&
          rows.map((row) => (
            <div
              key={`${row.mode}-${row.id}`}
              className={[styles.row, canOpen ? styles.clickable : ''].join(' ')}
              onClick={() => open(row)}
            >
              {/**
               * 🔑 La insignia lleva **letras**, no solo color: un badge que solo se distingue por tono deja fuera
               * a quien no separa bien los colores, y se pierde en una captura en blanco y negro.
               */}
              <span className={[styles.tag, styles[`tag_${MODE[row.mode].tone}`]].join(' ')}>
                {MODE[row.mode].tag}
              </span>
              <span className={styles.reference}>{row.reference}</span>
              {/**
               * 🔄 15-oct-2026: el estatus, con el color de la tabla de cargas (`statusColor`). Antes salía "—" en
               * todas las cargas porque el backend no lo mandaba, y se leía como un dato que faltaba.
               *
               * 📌 La fecha es la del compromiso: el Protect Time en terrestre y la ETA en marítimo y aéreo. Lo dice al
               * pasar el cursor.
               */}
              <span className={styles.meta}>
                {row.status && (
                  <span
                    className={styles.status}
                    style={{ '--status-color': statusColor(row.status, row.mode) } as React.CSSProperties}
                  >
                    {row.status}
                  </span>
                )}
                {row.expectedAt && (
                  <span className={styles.when} title={row.mode === 'load' ? 'Protect Time' : 'ETA'}>
                    {shortDay(row.expectedAt)}
                  </span>
                )}
              </span>
            </div>
          ))}
      </div>

      {/**
       * ⚠️ Paginado con "anterior / siguiente" y **sin números de página**. No hay total exacto cuando hay
       * búsqueda —contarlo exigiría el mismo `LIKE` en las tres tablas— y pintar "página 3 de 74" con un total
       * inventado es peor que no pintarlo. Lo que se necesita saber es si hay más.
       */}
      {(page > 1 || hasMore) && (
        <div className={styles.pager}>
          <button
            type="button"
            className={styles.pagerBtn}
            disabled={page === 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className={styles.pagerAt}>Page {page}</span>
          <button
            type="button"
            className={styles.pagerBtn}
            disabled={!hasMore || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

export default ShipmentsColumn;
